import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/config';
import { useCustomers } from '../../context/CustomerContext';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { calculateVisitFrequencyRole } from '../../utils/loyalty';
import { RefreshCw, Lock, Users, ScrollText, TrendingUp, UserPlus, ShoppingCart, Award } from 'lucide-react';
import CustomerLogs from './CustomerLogs';

// Sub-components
import FilterBar from './customers/FilterBar';
import CustomerTable from './customers/CustomerTable';
import CustomerDetailModal from './customers/CustomerDetailModal';
import AddCustomerModal from './customers/AddCustomerModal';
import QRScanner from '../../components/QRScanner';

const ITEMS_PER_PAGE = 25;

const Customers = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { customers, loading, addCustomer, deleteCustomer, updateCustomer, syncAllCustomersMetadata } = useCustomers();
    const { userData, tenantData, hasPermission, permissionsLoading, effectiveTenantId } = useAuth();
    const { showAlert, showConfirm } = useAlert();

    // Tab derived from URL
    const activeTab = location.pathname.includes('customer-logs') ? 'logs' : 'list';

    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setModalOpen] = useState(location.state?.openModal || false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isScannerOpen, setScannerOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const [selectedSegments, setSelectedSegments] = useState([]);
    const [tenants, setTenants] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (!effectiveTenantId && userData?.isSuperAdmin) {
            supabase.from('tenants').select('id, name').then(({ data }) => {
                const map = {};
                (data || []).forEach(r => map[r.id] = r.name);
                setTenants(map);
            });
        }
    }, [effectiveTenantId, userData?.isSuperAdmin]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedSegments, sortConfig]);

    const filteredCustomers = useMemo(() => {
        let result = (customers || []).filter(c => {
            const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
            const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) || (c.phone || '').includes(searchTerm) || (c.shortId || '').toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch) return false;

            if (selectedSegments.length > 0) {
                const matchesSegment = selectedSegments.some(seg => {
                    if (seg === 'new') return c.visitRole === 'Yeni';
                    if (seg === 'old') return c.visitRole !== 'Yeni' && c.visitRole !== 'Potansiyel';
                    if (seg === 'loyal') return c.visitRole === 'Müdavim' || c.visitRole === 'Normal';
                    if (seg === 'risk') return c.visitRole === 'Riskli';
                    return true;
                });
                if (!matchesSegment) return false;
            }

            return true;
        });

        result.sort((a, b) => {
            let vA, vB;
            if (sortConfig.key === 'name') {
                vA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
                vB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
            }
            else if (sortConfig.key === 'createdAt') {
                const getVal = (d) => d ? (d.toDate ? d.toDate().getTime() : new Date(d).getTime()) : 0;
                vA = getVal(a.createdAt);
                vB = getVal(b.createdAt);
            }
            else if (sortConfig.key === 'points') { vA = a.points || 0; vB = b.points || 0; }
            else if (sortConfig.key === 'avgBasket') { vA = (a.totalSpent || 0) / (a.purchaseCount || 1); vB = (b.totalSpent || 0) / (b.purchaseCount || 1); }
            else if (sortConfig.key === 'lastVisit') {
                const getVal = (d) => d ? new Date(d).getTime() : 0;
                vA = getVal(a.lastInteractionAt);
                vB = getVal(b.lastInteractionAt);
            }
            else if (sortConfig.key === 'totalSpent') { vA = a.totalSpent || 0; vB = b.totalSpent || 0; }

            if (vA < vB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (vA > vB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return result;
    }, [customers, searchTerm, sortConfig, selectedSegments]);

    // Pagination
    const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);
    const paginatedCustomers = filteredCustomers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    // Summary stats
    const stats = useMemo(() => {
        const all = customers || [];
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const newThisMonth = all.filter(c => c.createdAt && new Date(c.createdAt) >= thirtyDaysAgo).length;
        const totalSpent = all.reduce((sum, c) => sum + (c.totalSpent || 0), 0);
        const totalWithPurchases = all.filter(c => c.purchaseCount > 0);
        const avgBasket = totalWithPurchases.length > 0
            ? totalWithPurchases.reduce((sum, c) => sum + ((c.totalSpent || 0) / (c.purchaseCount || 1)), 0) / totalWithPurchases.length
            : 0;
        const totalPoints = all.reduce((sum, c) => sum + (c.points || 0), 0);
        return { total: all.length, newThisMonth, totalSpent, avgBasket, totalPoints };
    }, [customers]);

    const handleAddCustomer = async (data) => {
        try {
            await addCustomer(data);
            setModalOpen(false);
        } catch (err) { showAlert(err.message === 'FREE_PLAN_LIMIT_REACHED' ? t('planLimits.customerLimitReached') : "Hata!", 'error'); }
    };

    if (loading || permissionsLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><RefreshCw className="animate-spin" /></div>;
    if (!userData?.isSuperAdmin && !hasPermission('customers', 'view')) return <div style={{ textAlign: 'center', padding: '5rem' }}><Lock size={48} /><h3>Erişim Engellendi</h3></div>;

    const tabBtnStyle = (tab) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '10px 24px',
        borderRadius: '12px',
        border: 'none',
        background: activeTab === tab ? 'var(--primary)' : 'transparent',
        color: activeTab === tab ? 'var(--text-on-primary)' : 'var(--text-main)',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
    });

    const statCards = [
        { icon: <Users size={20} />, label: 'Toplam Müşteri', value: stats.total, color: 'var(--primary)' },
        { icon: <UserPlus size={20} />, label: 'Son 30 Gün', value: `+${stats.newThisMonth}`, color: '#10b981' },
        { icon: <ShoppingCart size={20} />, label: 'Ort. Sepet', value: `₺${stats.avgBasket.toFixed(0)}`, color: '#f59e0b' },
        { icon: <TrendingUp size={20} />, label: 'Toplam Harcama', value: `₺${stats.totalSpent.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, color: '#3b82f6' },
    ];

    return (
        <div className="fade-in">
            <header style={{ marginBottom: '2rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                    <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
                        {activeTab === 'list' ? t('customers.title') : 'Müşteri Kayıtları'}
                    </h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
                        {activeTab === 'list' ? t('customers.subtitle') : 'Müşterilerinizle ilgili tüm işlem geçmişi ve aktiviteler.'}
                    </p>
                </div>
            </header>

            <style>{`
                .settings-header-actions .short-text, .customers-tabs-nav .short-text { display: none; }
                .search-container { max-width: 500px; width: 100%; }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }
                .stat-card {
                    background: var(--bg-card);
                    border: 1px solid var(--border);
                    border-radius: 16px;
                    padding: 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    transition: all 0.2s;
                }
                .stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
                }
                .stat-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .pagination-container {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid var(--border);
                    background: var(--bg-card);
                    border-radius: 0 0 12px 12px;
                    flex-wrap: wrap;
                    gap: 0.75rem;
                }
                .pagination-btn {
                    padding: 6px 14px;
                    border-radius: 8px;
                    border: 1px solid var(--border);
                    background: var(--bg-card);
                    color: var(--text-main);
                    font-weight: 600;
                    font-size: 0.8125rem;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .pagination-btn:hover:not(:disabled) {
                    background: rgba(var(--primary-rgb), 0.08);
                    border-color: var(--primary);
                    color: var(--primary);
                }
                .pagination-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }
                .pagination-btn.active {
                    background: var(--primary);
                    color: var(--text-on-primary);
                    border-color: var(--primary);
                }

                @media (max-width: 768px) {
                    .stats-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                        gap: 0.75rem !important;
                    }
                    .stat-card {
                        padding: 1rem !important;
                    }
                    .stat-icon {
                        width: 36px !important;
                        height: 36px !important;
                    }
                    .promotions-controls-row { flex-direction: column-reverse !important; }
                    .search-container { width: 100% !important; max-width: none !important; }
                    .settings-header-actions { width: 100% !important; display: flex !important; flex-direction: row !important; gap: 8px !important; margin-top: 1rem !important; }
                    .settings-header-actions button, .settings-header-actions > div { flex: 1 !important; width: auto !important; height: auto !important; min-height: 46px !important; }
                    .settings-header-actions button { padding: 8px 4px !important; white-space: normal !important; text-align: center !important; justify-content: center !important; font-size: 0.75rem !important; }
                    .settings-header-actions .full-text, .customers-tabs-nav .full-text { display: none !important; }
                    .settings-header-actions .short-text, .customers-tabs-nav .short-text { display: inline !important; }
                    .settings-header-actions > div { margin-left: 0 !important; }
                    .settings-header-actions > div button { width: 100% !important; }

                    .customers-tabs-nav { width: 100% !important; padding: 0.3rem !important; gap: 0.2rem !important; overflow: hidden !important; }
                    .customers-tabs-nav .tab-btn {
                        flex: 1 !important;
                        flex-direction: column !important;
                        padding: 8px 1px !important;
                        font-size: 0.65rem !important;
                        gap: 4px !important;
                    }
                    .customers-tabs-nav .tab-icon svg { width: 16px !important; height: 16px !important; }
                    .pagination-container {
                        justify-content: center !important;
                    }
                }
            `}</style>

            {/* Tab navigation */}
            <div style={{ marginBottom: '1.25rem' }}>
                <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    background: 'rgba(var(--primary-rgb), 0.05)',
                    padding: '0.5rem',
                    borderRadius: '16px',
                    width: 'fit-content',
                    maxWidth: '100%'
                }} className="customers-tabs-nav">
                    <button
                        onClick={() => navigate('/app/customers')}
                        style={tabBtnStyle('list')}
                        className="tab-btn"
                    >
                        <span className="tab-icon" style={{ display: 'flex', alignItems: 'center' }}><Users size={18} /></span>
                        <span className="tab-label">
                            <span className="full-text">Müşteri Listesi</span>
                            <span className="short-text">Müşteriler</span>
                        </span>
                    </button>
                    <button
                        onClick={() => navigate('/app/customer-logs')}
                        style={tabBtnStyle('logs')}
                        className="tab-btn"
                    >
                        <span className="tab-icon" style={{ display: 'flex', alignItems: 'center' }}><ScrollText size={18} /></span>
                        <span className="tab-label">
                            <span className="full-text">Müşteri Kayıtları</span>
                            <span className="short-text">Loglar</span>
                        </span>
                    </button>
                </div>
            </div>

            {/* Logs tab content */}
            {activeTab === 'logs' && <CustomerLogs hideHeader />}

            {/* Customer list tab content */}
            {activeTab === 'list' && (
                <>
                    {/* Summary Stats */}
                    <div className="stats-grid">
                        {statCards.map((s, i) => (
                            <div key={i} className="stat-card">
                                <div className="stat-icon" style={{ background: `${s.color}12`, color: s.color }}>
                                    {s.icon}
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '2px' }}>{s.label}</div>
                                    <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)', letterSpacing: '-0.02em' }}>{s.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <FilterBar
                        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                        onAddClick={() => setModalOpen(true)} onScannerOpen={() => setScannerOpen(true)}
                        filterMenuOpen={filterMenuOpen} setFilterMenuOpen={setFilterMenuOpen}
                        selectedSegments={selectedSegments} setSelectedSegments={setSelectedSegments}
                        toggleSegment={(s) => setSelectedSegments(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                        hasCreatePermission={hasPermission('customers', 'create')} t={t}
                        onSyncAll={syncAllCustomersMetadata}
                        totalCount={filteredCustomers.length}
                    />

                    <CustomerTable
                        customers={paginatedCustomers} tenantData={tenantData} userData={userData}
                        tenants={tenants} effectiveTenantId={effectiveTenantId}
                        sortConfig={sortConfig} onSort={(key) => setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        onSelect={setSelectedCustomer} t={t}
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                {filteredCustomers.length} müşteriden {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredCustomers.length)} arası gösteriliyor
                            </span>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <button className="pagination-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>{'«'}</button>
                                <button className="pagination-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>{'‹'}</button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let page;
                                    if (totalPages <= 5) {
                                        page = i + 1;
                                    } else if (currentPage <= 3) {
                                        page = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        page = totalPages - 4 + i;
                                    } else {
                                        page = currentPage - 2 + i;
                                    }
                                    return (
                                        <button key={page} className={`pagination-btn ${currentPage === page ? 'active' : ''}`} onClick={() => setCurrentPage(page)}>
                                            {page}
                                        </button>
                                    );
                                })}
                                <button className="pagination-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>{'›'}</button>
                                <button className="pagination-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>{'»'}</button>
                            </div>
                        </div>
                    )}

                    <AddCustomerModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} onAdd={handleAddCustomer} t={t} />

                    <CustomerDetailModal
                        customer={selectedCustomer} tenantData={tenantData}
                        onClose={() => setSelectedCustomer(null)}
                        onDelete={async (c) => { if (await showConfirm("Bu müşteriyi silmek istediğinize emin misiniz?", { title: 'Müşteri Sil', confirmText: 'Sil', type: 'error' })) { await deleteCustomer(c.id); setSelectedCustomer(null); } }}
                        onUpdate={async (id, updates) => { await updateCustomer(id, updates); }}
                        canDelete={hasPermission('customers', 'delete')} t={t}
                    />

                    {isScannerOpen && <QRScanner onScan={(id) => { const c = customers.find(x => x.id === id || x.shortId === id); if (c) setSelectedCustomer(c); setScannerOpen(false); }} onClose={() => setScannerOpen(false)} />}
                </>
            )}
        </div>
    );
};

export default Customers;
